import * as React from 'react'

import {
  EventBlock,
  EventSchemaHelpers,
  RangeEnum,
} from '@basehub/data/replicache/index'
import { getEventChartQueryKey } from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/blocks/event/time-series'
import { useRepoReplicache } from './use-repo-replicache'
import { useUserPreferences } from './use-user-preferences'
import { useQueryClient } from '@tanstack/react-query'

const eventsMap = new Map<
  string,
  { values: EventSchemaHelpers.EventNode[]; idleValue: number }
>()

export const useGetEventData = (params?: { onFetched: () => void }) => {
  const queryClient = useQueryClient()
  const team = useRepoReplicache()?.data?.team
  const repo = useRepoReplicache()?.data?.repo

  const getEventRange = useUserPreferences(
    (s) => (id: string) => s.eventSelectedRange[id] ?? RangeEnum.month
  )
  const highestEventValue = React.useRef(0)

  const getEventData = React.useCallback(
    (
      block: EventBlock,
      templateBlock: EventBlock = block
    ): {
      isLoading: boolean
      data: { values: EventSchemaHelpers.EventNode[]; idleValue: number }
    } => {
      const range = getEventRange(templateBlock.id)
      const eventBlockData = queryClient.getQueryData(
        getEventChartQueryKey({
          blockId: block.id,
          teamId: team?.id,
          repoId: repo?.id,
          range,
        })
      ) as
        | {
            events: EventSchemaHelpers.EventNode[]
            resolvedRange: string
          }
        | undefined

      if (eventBlockData) {
        const { allBreakpoints, total } = EventSchemaHelpers.fillTimePeriod(
          eventBlockData.events,
          range
        )

        highestEventValue.current = Math.max(
          highestEventValue.current,
          ...allBreakpoints.map(({ count }) => count)
        )
        return {
          isLoading: false,
          data: { values: allBreakpoints, idleValue: total },
        }
      }
      if (eventsMap.has(block.id)) {
        const { allBreakpoints, total } = EventSchemaHelpers.fillTimePeriod(
          eventsMap.get(block.id)!.values,
          range
        )
        highestEventValue.current = Math.max(
          highestEventValue.current,
          ...allBreakpoints.map(({ count }) => count)
        )
        return {
          isLoading: false,
          data: { values: allBreakpoints, idleValue: total },
        }
      }
      if (!team || !repo) {
        return { isLoading: false, data: { values: [], idleValue: 0 } }
      }

      eventsMap.set(block.id, { values: [], idleValue: 0 })
      fetch(
        `/api/v2/events/query?block-id=${block.id}&team-id=${team.id}&repo-id=${repo.id}&range=${range}`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        }
      )
        .then(async (res) => {
          if (!res.ok) return []
          return (await res.json()) as
            | { success: true; data: EventSchemaHelpers.EventNode[] }
            | { error: string }
        })
        .then((res) => {
          if ('error' in res) return []
          if (!('success' in res)) return []
          const { data } = res

          const { total, allBreakpoints } = EventSchemaHelpers.fillTimePeriod(
            data,
            range
          )
          highestEventValue.current = Math.max(
            highestEventValue.current,
            ...allBreakpoints.map(({ count }) => count)
          )
          eventsMap.set(block.id, { values: allBreakpoints, idleValue: total })
          params?.onFetched()
          return data
        })

      return { isLoading: true, data: { values: [], idleValue: 0 } }
    },
    [getEventRange, params, queryClient, repo, team]
  )

  return { highestEventValue, getEventData }
}
